import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  addMediaToEntity,
  bulkUpdateEntities,
  deleteEntityNoHandle,
  getPredefinedTemplate,
  importKML,
  regroupEntityById,
  relinkEntity,
  searchEntityById,
  searchMapEntities,
  upsertEntityNoHandle,
} from 'api/entities';
import { predefinedTemplates } from 'constants/entities';
import { errorMessages } from 'constants/errors';
import {
  HIERARCHY_INITIAL_DEPTH,
  HIERARCHY_MAX_DEPTH,
  ROOT_HIERARCHY_ID,
} from 'constants/hierarchy';
import {
  CREATE_MAP_QUICK_ENTITY_LOADER,
  MAP_ENTITIES_LOADER,
} from 'constants/loaders';
import { asyncActionsNames, reducersNames } from 'constants/reducers';
import {
  IEntityAddMediaRequest,
  IEntitySearchRequest,
  IFilterRequest,
  UpsertEntityRequest,
} from 'interfaces';
import { MAP_V2_REDUCER_NAMES } from 'store/slices/mapV2/constants';
import { loadersActions } from 'store/slices/service/loadersSlice';
import { AsyncThunkConfig, ThunkAPI } from 'types';
import {
  Entity,
  PredefinedTemplate,
  UpsertEntityResponse,
} from 'types/entities';

import { buildThunk, createHierarchy } from 'utils';

import { RootState } from '../../../../../index';
import { TABS_REDUCER_NAMES } from '../../constants';
import { LAYERS_REDUCER_NAMES } from '../constants';
import { filtersSelector } from '../stateSlice/selectors';

import { predefinedTemplateIdSelector } from './selectors';
import {
  CreateMapEntityRequest,
  CreateMapEntityResponse,
  GetAllMapEntitiesRequest,
  GetMapEntitiesRequest,
  GetMapEntitiesResponse,
  GetMapEntityChildrenResponse,
  GetSharedMapEntityResponse,
  ImportMapEntitiesRequest,
  RegroupMapEntitiesResponse,
  RelinkMapEntityRequest,
  UpdateMapEntitiesBulkRequest,
} from './types';

const applyMapEntitiesTemplateFilter = (
  request: IEntitySearchRequest = {},
  thunkAPI: ThunkAPI
): IEntitySearchRequest => {
  if (!('getState' in thunkAPI)) {
    return request;
  }

  const state = thunkAPI.getState() as RootState;
  const templateFilter = [
    predefinedTemplateIdSelector(state, predefinedTemplates.MAP_OBJECT),
    predefinedTemplateIdSelector(state, predefinedTemplates.MAP_LAYER),
  ].flatMap((v) => (v ? [v] : []));

  return {
    templateIDs: templateFilter,
    ...request,
  };
};

const applyMapEntitiesFilters = (
  request: IEntitySearchRequest = {},
  thunkAPI: ThunkAPI
): IEntitySearchRequest => {
  if (!('getState' in thunkAPI)) {
    return request;
  }

  const state = thunkAPI.getState() as RootState;
  const filters = filtersSelector(state);

  return {
    ...filters,
    ...request,
  };
};

const applyMapObjectEntitiesCounter = (
  request: IEntitySearchRequest = {},
  thunkAPI: ThunkAPI
): IEntitySearchRequest => {
  if (!('getState' in thunkAPI)) {
    return request;
  }

  const state = thunkAPI.getState() as RootState;
  const idsToCount = [
    predefinedTemplateIdSelector(state, predefinedTemplates.MAP_OBJECT),
  ].flatMap((v) => (v ? [v] : []));

  return {
    withCounters: true,
    countTemplateIDs: idsToCount,
    countFromEntityIDs: [0],
    ...request,
  };
};

const getMapEntitiesWithFilters = (
  request: IEntitySearchRequest | undefined,
  thunkAPI: ThunkAPI
) => {
  const requestWithTemplateFilter = applyMapEntitiesTemplateFilter(
    request,
    thunkAPI
  );

  const requestWithFilters = applyMapEntitiesFilters(
    requestWithTemplateFilter,
    thunkAPI
  );

  return searchMapEntities(requestWithFilters);
};

// The only way to get actual hierarchy and counters after create/update is to get full three
const getMapEntitiesWithFiltersAndCounters = (
  request: IEntitySearchRequest | undefined,
  thunkAPI: ThunkAPI
) => {
  const requestWithTemplateFilter = applyMapEntitiesTemplateFilter(
    request,
    thunkAPI
  );

  const requestWithFilters = applyMapEntitiesFilters(
    requestWithTemplateFilter,
    thunkAPI
  );

  const requestWithCounters = applyMapObjectEntitiesCounter(
    requestWithFilters,
    thunkAPI
  );

  return searchMapEntities(requestWithCounters);
};

export const getPredefinedTemplateThunk = buildThunk<
  PredefinedTemplate,
  predefinedTemplates
>(
  `${reducersNames.MAP_V2}`,
  errorMessages.GET_PREDEFINED_TEMPLATES_ERROR,
  getPredefinedTemplate
);

export const getInitialMapEntitiesThunk = buildThunk<
  GetMapEntitiesResponse,
  GetMapEntitiesRequest
>(
  `${reducersNames.MAP_V2}/${MAP_V2_REDUCER_NAMES.TABS}/${TABS_REDUCER_NAMES.LAYERS}/${LAYERS_REDUCER_NAMES.MAP_ENTITIES}/${asyncActionsNames.GET_INITIAL_MAP_ENTITIES}`,
  errorMessages.GET_MAP_ENTITIES_ERROR,
  (request, thunkAPI) =>
    getMapEntitiesWithFiltersAndCounters(
      { ...request, maxDepth: HIERARCHY_INITIAL_DEPTH },
      thunkAPI
    ),
  (request, thunkAPI) =>
    thunkAPI.dispatch?.(
      loadersActions.addLoader({
        id: MAP_ENTITIES_LOADER,
      })
    ),
  (_, thunkAPI) =>
    thunkAPI.dispatch?.(loadersActions.removeLoader(MAP_ENTITIES_LOADER))
);

export const getAllMapEntitiesThunk = buildThunk<
  GetMapEntitiesResponse,
  GetAllMapEntitiesRequest
>(
  `${reducersNames.MAP_V2}/${MAP_V2_REDUCER_NAMES.TABS}/${TABS_REDUCER_NAMES.LAYERS}/${LAYERS_REDUCER_NAMES.MAP_ENTITIES}/${asyncActionsNames.GET_ALL_MAP_ENTITIES}`,
  errorMessages.GET_MAP_ENTITIES_ERROR,
  (params, thunkAPI) =>
    getMapEntitiesWithFiltersAndCounters(
      { ...params, maxDepth: HIERARCHY_MAX_DEPTH },
      thunkAPI
    )
);

export const getFilteredMapEntitiesThunk = buildThunk<
  GetMapEntitiesResponse,
  IFilterRequest
>(
  `${reducersNames.MAP_V2}/${MAP_V2_REDUCER_NAMES.TABS}/${TABS_REDUCER_NAMES.LAYERS}/${LAYERS_REDUCER_NAMES.MAP_ENTITIES}/${asyncActionsNames.GET_FILTERED_MAP_ENTITIES}`,
  errorMessages.GET_MAP_FILTERED_ENTITIES_ERROR,
  (request, thunkAPI) =>
    getMapEntitiesWithFiltersAndCounters(
      { ...request, maxDepth: HIERARCHY_INITIAL_DEPTH },
      thunkAPI
    ),
  (request, thunkAPI) =>
    thunkAPI.dispatch?.(
      loadersActions.addLoader({
        id: MAP_ENTITIES_LOADER,
      })
    ),
  (_, thunkAPI) =>
    thunkAPI.dispatch?.(loadersActions.removeLoader(MAP_ENTITIES_LOADER))
);

export const getSharedMapEntityThunk = createAsyncThunk<
  GetSharedMapEntityResponse,
  number,
  AsyncThunkConfig
>(
  `${reducersNames.MAP_V2}/${MAP_V2_REDUCER_NAMES.TABS}/${TABS_REDUCER_NAMES.LAYERS}/${LAYERS_REDUCER_NAMES.MAP_ENTITIES}/${asyncActionsNames.GET_SHARED_ENTITY}`,
  async (params, thunkAPI) => {
    try {
      thunkAPI.dispatch?.(
        loadersActions.addLoader({
          id: MAP_ENTITIES_LOADER,
        })
      );

      const res = await searchEntityById(params);
      const parentEntityId = res.data.entities[0].upmostParentID;
      const { data } = await getMapEntitiesWithFilters(
        {
          parentEntityID: parentEntityId,
          maxDepth: HIERARCHY_MAX_DEPTH,
        },
        thunkAPI
      );

      return { ...data, targetId: params };
    } catch (error) {
      return thunkAPI.rejectWithValue({
        error,
        message: errorMessages.GET_ENTITY_ERROR,
      });
    } finally {
      thunkAPI.dispatch(loadersActions.removeLoader(MAP_ENTITIES_LOADER));
    }
  }
);

export const getInscribedMapEntities = buildThunk<
  GetMapEntitiesResponse,
  GetMapEntitiesRequest
>(
  `${reducersNames.MAP_V2}/${MAP_V2_REDUCER_NAMES.TABS}/${TABS_REDUCER_NAMES.LAYERS}/${LAYERS_REDUCER_NAMES.MAP_ENTITIES}/${asyncActionsNames.GET_INSCRIBED_MAP_ENTITIES}`,
  errorMessages.GET_MAP_INSCRIBED_ENTITIES_ERROR,
  getMapEntitiesWithFilters,
  (request, thunkAPI) =>
    thunkAPI.dispatch?.(
      loadersActions.addLoader({
        id: MAP_ENTITIES_LOADER,
      })
    ),
  (_, thunkAPI) =>
    thunkAPI.dispatch?.(loadersActions.removeLoader(MAP_ENTITIES_LOADER))
);

export const openMapFolderEntityThunk = buildThunk<
  GetMapEntityChildrenResponse,
  GetMapEntitiesRequest
>(
  `${reducersNames.MAP_V2}/${MAP_V2_REDUCER_NAMES.TABS}/${TABS_REDUCER_NAMES.LAYERS}/${LAYERS_REDUCER_NAMES.MAP_ENTITIES}/${asyncActionsNames.OPEN_MAP_FOLDER_ENTITY}`,
  errorMessages.GET_MAP_ENTITIES_ERROR,
  getMapEntitiesWithFilters
);

export const selectMapFolderEntityThunk = buildThunk<
  GetMapEntityChildrenResponse,
  GetMapEntitiesRequest
>(
  `${reducersNames.MAP_V2}/${MAP_V2_REDUCER_NAMES.TABS}/${TABS_REDUCER_NAMES.LAYERS}/${LAYERS_REDUCER_NAMES.MAP_ENTITIES}/${asyncActionsNames.SELECT_MAP_FOLDER_ENTITY}`,
  errorMessages.GET_MAP_ENTITIES_ERROR,
  (params, thunkAPI) =>
    getMapEntitiesWithFilters(
      { ...params, maxDepth: HIERARCHY_MAX_DEPTH },
      thunkAPI
    )
);

export const createMapEntityThunk = createAsyncThunk<
  CreateMapEntityResponse,
  CreateMapEntityRequest,
  AsyncThunkConfig
>(
  `${reducersNames.MAP_V2}/${MAP_V2_REDUCER_NAMES.TABS}/${TABS_REDUCER_NAMES.LAYERS}/${LAYERS_REDUCER_NAMES.MAP_ENTITIES}/${asyncActionsNames.CREATE_MAP_ENTITY}`,
  async (params, thunkAPI) => {
    try {
      const { parentEntityID, parameters, mediaParamKeyID } = params;

      const res = await upsertEntityNoHandle({
        ...params,
        parameters: {
          ...parameters,
          ...(mediaParamKeyID &&
            parameters[mediaParamKeyID]?.value?.length && {
              [mediaParamKeyID]: { value: [] },
            }),
        },
      });

      if (mediaParamKeyID && parameters[mediaParamKeyID]?.value?.length) {
        const mediaFiles = parameters[mediaParamKeyID].value;

        for (let index = 0; index < mediaFiles.length; index++) {
          const currentMedia = mediaFiles[index];

          currentMedia.file &&
            (await addMediaToEntity({
              entityID: res.data.id,
              paramID: Number(mediaParamKeyID),
              file: currentMedia.file,
              mediaArrayIndex: index,
            }));
        }
      }

      const upmostParentId = res.data.upmostParentID ?? ROOT_HIERARCHY_ID;
      const { data } = await getMapEntitiesWithFiltersAndCounters(
        {
          parentEntityID,
          maxDepth: HIERARCHY_MAX_DEPTH,
          countFromEntityIDs: [upmostParentId],
        },
        thunkAPI
      );

      const hierarchy = parentEntityID
        ? data.hierarchy
        : [createHierarchy(ROOT_HIERARCHY_ID, data.hierarchy)];

      return {
        ...data,
        hierarchy: hierarchy,
        targetId: res.data.id,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue({
        error,
        message: errorMessages.ENTITY_CREATION_ERROR,
      });
    }
  }
);

export const createQuickMapEntityThunk = createAsyncThunk<
  CreateMapEntityResponse,
  CreateMapEntityRequest,
  AsyncThunkConfig
>(
  `${reducersNames.MAP_V2}/${MAP_V2_REDUCER_NAMES.TABS}/${TABS_REDUCER_NAMES.LAYERS}/${LAYERS_REDUCER_NAMES.MAP_ENTITIES}/${asyncActionsNames.CREATE_QUICK_MAP_ENTITY}`,
  async (params, thunkAPI) => {
    const { parentEntityID } = params;
    const isCreatingQuickEntity = (thunkAPI.getState() as RootState).service
      .loaders.loaders[CREATE_MAP_QUICK_ENTITY_LOADER];

    if (isCreatingQuickEntity) {
      return thunkAPI.rejectWithValue({
        error: errorMessages.ENTITY_CREATION_PENDING_ERROR,
        message: errorMessages.ENTITY_CREATION_PENDING_ERROR,
      });
    }

    try {
      thunkAPI.dispatch(
        loadersActions.addLoader({ id: CREATE_MAP_QUICK_ENTITY_LOADER })
      );

      const res = await upsertEntityNoHandle(params);
      const upmostParentId = res.data.upmostParentID ?? ROOT_HIERARCHY_ID;
      const { data } = await getMapEntitiesWithFiltersAndCounters(
        {
          parentEntityID,
          maxDepth: HIERARCHY_MAX_DEPTH,
          countFromEntityIDs: [upmostParentId],
        },
        thunkAPI
      );

      const hierarchy = parentEntityID
        ? data.hierarchy
        : [createHierarchy(ROOT_HIERARCHY_ID, data.hierarchy)];

      return {
        ...data,
        hierarchy: hierarchy,
        targetId: res.data.id,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue({
        error,
        message: errorMessages.ENTITY_CREATION_ERROR,
      });
    } finally {
      thunkAPI.dispatch(
        loadersActions.removeLoader(CREATE_MAP_QUICK_ENTITY_LOADER)
      );
    }
  }
);

export const importMapEntitiesThunk = createAsyncThunk<
  GetMapEntitiesResponse,
  ImportMapEntitiesRequest,
  AsyncThunkConfig
>(
  `${reducersNames.MAP_V2}/${MAP_V2_REDUCER_NAMES.TABS}/${TABS_REDUCER_NAMES.LAYERS}/${LAYERS_REDUCER_NAMES.MAP_ENTITIES}/${asyncActionsNames.IMPORT_MAP_ENTITIES}`,
  async (params, thunkAPI) => {
    const {
      file,
      params: {
        upmostParentId = ROOT_HIERARCHY_ID,
        parentId,
        entityID,
        file: extension,
        filename,
        ignoreMedia,
      },
    } = params;

    try {
      await importKML(file, {
        entityID,
        file: extension,
        filename,
        ignoreMedia,
      });

      const { data } = await getMapEntitiesWithFiltersAndCounters(
        {
          parentEntityID: parentId,
          maxDepth: HIERARCHY_MAX_DEPTH,
          countFromEntityIDs: [upmostParentId],
        },
        thunkAPI
      );

      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        error,
        message: errorMessages.ENTITY_CREATION_ERROR,
      });
    }
  }
);

export const updateMapEntityThunk = buildThunk<
  UpsertEntityResponse,
  UpsertEntityRequest
>(
  `${reducersNames.MAP_V2}/${MAP_V2_REDUCER_NAMES.TABS}/${TABS_REDUCER_NAMES.LAYERS}/${LAYERS_REDUCER_NAMES.MAP_ENTITIES}/${asyncActionsNames.UPDATE_MAP_ENTITY}`,
  errorMessages.ENTITY_UPDATE_ERROR,
  upsertEntityNoHandle
);

export const updateMapEntitiesBulkThunk = createAsyncThunk<
  GetMapEntitiesResponse,
  UpdateMapEntitiesBulkRequest,
  AsyncThunkConfig
>(
  `${reducersNames.MAP_V2}/${MAP_V2_REDUCER_NAMES.TABS}/${TABS_REDUCER_NAMES.LAYERS}/${LAYERS_REDUCER_NAMES.MAP_ENTITIES}/${asyncActionsNames.UPDATE_MAP_ENTITIES_BULK}`,
  async (params, thunkAPI) => {
    try {
      await bulkUpdateEntities(params);

      const { data } = await getMapEntitiesWithFilters(
        {
          parentEntityID: params.parentEntityID,
          maxDepth: HIERARCHY_MAX_DEPTH,
        },
        thunkAPI
      );

      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        error,
        message: errorMessages.ENTITIES_UPDATE_ERROR,
      });
    }
  }
);

export const relinkEntityThunk = createAsyncThunk<
  GetMapEntitiesResponse,
  RelinkMapEntityRequest,
  AsyncThunkConfig
>(
  `${reducersNames.MAP_V2}/${MAP_V2_REDUCER_NAMES.TABS}/${TABS_REDUCER_NAMES.LAYERS}/${LAYERS_REDUCER_NAMES.MAP_ENTITIES}/${asyncActionsNames.RELINK_MAP_ENTITY}`,
  async (params, thunkAPI) => {
    try {
      const {
        entityId,
        oldParentId,
        newParentId,
        oldUpmostParentId,
        newUpmostParentId,
        upperLevelNodeId,
      } = params;

      const isMovingFromRoot = oldParentId === ROOT_HIERARCHY_ID;
      const isMovingToRoot = newParentId === ROOT_HIERARCHY_ID;

      await relinkEntity(entityId, isMovingToRoot ? undefined : newParentId);

      if (isMovingFromRoot || isMovingToRoot) {
        const res = await getMapEntitiesWithFiltersAndCounters(
          {
            maxDepth: HIERARCHY_MAX_DEPTH,
          },
          thunkAPI
        );

        return {
          ...res.data,
          hierarchy: [createHierarchy(ROOT_HIERARCHY_ID, res.data.hierarchy)],
        };
      }

      if (oldUpmostParentId === newUpmostParentId && upperLevelNodeId) {
        const { data } = await getMapEntitiesWithFiltersAndCounters(
          {
            parentEntityID: upperLevelNodeId,
            maxDepth: HIERARCHY_MAX_DEPTH,
            countFromEntityIDs: [newUpmostParentId ?? ROOT_HIERARCHY_ID],
          },
          thunkAPI
        );

        const { entities, hierarchy, counters } = data;

        return {
          entities: [...entities],
          hierarchy: [hierarchy[0]],
          counters: {
            ...counters,
          },
        };
      }

      const oldParentRes = await getMapEntitiesWithFiltersAndCounters(
        {
          parentEntityID: oldParentId,
          maxDepth: HIERARCHY_MAX_DEPTH,
          countFromEntityIDs: [oldUpmostParentId ?? ROOT_HIERARCHY_ID],
        },
        thunkAPI
      );

      const newParentRes = await getMapEntitiesWithFiltersAndCounters(
        {
          parentEntityID: newParentId,
          maxDepth: HIERARCHY_MAX_DEPTH,
          countFromEntityIDs: [newUpmostParentId ?? ROOT_HIERARCHY_ID],
        },
        thunkAPI
      );

      return {
        entities: [
          ...oldParentRes.data.entities,
          ...newParentRes.data.entities,
        ],
        hierarchy: [
          oldParentRes.data.hierarchy[0],
          newParentRes.data.hierarchy[0],
        ],
        counters: {
          ...oldParentRes.data.counters,
          ...newParentRes.data.counters,
        },
      };
    } catch (error) {
      return thunkAPI.rejectWithValue({
        error,
        message: errorMessages.ENTITY_RELINK_ERROR,
      });
    }
  }
);

export const regroupMapEntitiesThunk = createAsyncThunk<
  RegroupMapEntitiesResponse,
  number,
  AsyncThunkConfig
>(
  `${reducersNames.MAP_V2}/${MAP_V2_REDUCER_NAMES.TABS}/${TABS_REDUCER_NAMES.LAYERS}/${LAYERS_REDUCER_NAMES.MAP_ENTITIES}/${asyncActionsNames.REGROUP_MAP_ENTITIES}`,
  async (params, thunkAPI) => {
    try {
      const res = await regroupEntityById(params);
      const newParentId = res.data.newParentID;

      const { data } = await getMapEntitiesWithFilters(
        {
          parentEntityID: newParentId,
          maxDepth: HIERARCHY_MAX_DEPTH,
        },
        thunkAPI
      );

      return { ...data, parentId: newParentId };
    } catch (error) {
      return thunkAPI.rejectWithValue({
        error,
        message: errorMessages.ENTITY_REGROUP_ERROR,
      });
    }
  }
);

export const addMediaToEntityThunk = buildThunk<Entity, IEntityAddMediaRequest>(
  `${reducersNames.MAP_V2}/${MAP_V2_REDUCER_NAMES.TABS}/${TABS_REDUCER_NAMES.LAYERS}/${LAYERS_REDUCER_NAMES.MAP_ENTITIES}/${asyncActionsNames.ADD_MEDIA_TO_MAP_ENTITY}`,
  errorMessages.ENTITY_ADD_MEDIA_ERROR,
  addMediaToEntity
);

export const deleteEntityThunk = buildThunk<number, number>(
  `${reducersNames.MAP_V2}/${MAP_V2_REDUCER_NAMES.TABS}/${TABS_REDUCER_NAMES.LAYERS}/${LAYERS_REDUCER_NAMES.MAP_ENTITIES}/${asyncActionsNames.DELETE_ENTITY}`,
  errorMessages.ENTITY_DELETE_ERROR,
  // todo switch to function with proper naming
  deleteEntityNoHandle
);
